import { useCallback, useEffect, useState } from 'react';

import { describeApiError } from '../../lib/api-client';
import { useApiClient } from '../../providers/auth-provider';

import {
  Card,
  Empty,
  Metric,
  Pill,
  Tabs,
  fmtWhen,
  inputStyle,
  mono,
  primaryButton,
  secondaryButton,
} from '../proxy-vault/ui';

import type { SandboxPoolResponse } from '../../lib/api-client';

/**
 * One pool as its own workspace tab: identity (bundle, digest, names),
 * live numbers, and the lifecycle controls. Retire drains (no new claims,
 * leases finish); Delete removes the pool and its template — conversations
 * pinned to this bundle fall back to the environment default. History stays
 * in Sandbox Runtime Logs (runs carry bundle + pool + SDK versions).
 */
export function PoolDetailView({ poolId }: { poolId: string }) {
  const api = useApiClient();
  const [pool, setPool] = useState<SandboxPoolResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [replicas, setReplicas] = useState<string>('');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    api
      .getSandboxPool(poolId)
      .then((p) => {
        setPool(p);
        setError(null);
      })
      .catch((err) => setError(describeApiError(err, String(err))));
  }, [api, poolId]);

  useEffect(() => {
    refresh();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, 10_000);
    return () => clearInterval(id);
  }, [refresh]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      refresh();
    } catch (err) {
      setError(describeApiError(err, String(err)));
    } finally {
      setBusy(false);
    }
  };

  if (error && !pool) return <Empty>{error}</Empty>;
  if (!pool) return <Empty>Loading…</Empty>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Tabs tabs={[{ id: 'overview', label: 'Overview' }]} active="overview" onChange={() => {}} />
      <div style={{ flex: 1, overflow: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 860 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Metric value={`${pool.ready_replicas ?? '—'}/${pool.replicas}`} label="ready / desired" />
          <Metric value={pool.active} label="active" />
          <Metric value={pool.idle} label="idle" />
          <Metric
            value={<Pill tone={pool.status === 'ready' ? undefined : 'warn'}>{pool.status}</Pill>}
            label="status"
          />
        </div>
        <Card title="Bundle">
          <div style={{ fontSize: 12.5, fontFamily: mono, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div data-testid="pool-bundle-ref">{pool.bundle_ref}</div>
            <div style={{ color: 'var(--text-soft)' }}>digest: {pool.image_digest ?? 'not yet observed'}</div>
            <div style={{ color: 'var(--text-soft)' }}>
              template {pool.template_name} · pool {pool.pool_name} · key {pool.bundle_key}
            </div>
            <div style={{ color: 'var(--text-soft)' }}>
              worker SDK: {pool.worker_sdk_version ?? 'unknown'}
              {pool.worker_sdk_incompatible ? '  ⚠ below supported minimum' : ''}
            </div>
            <div style={{ color: 'var(--text-soft)' }}>
              first ready {fmtWhen(pool.first_ready_at)} · last claimed {fmtWhen(pool.last_claimed_at)} · created{' '}
              {fmtWhen(pool.created_at)}
            </div>
          </div>
        </Card>
        <Card title="Lifecycle">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                data-testid="pool-replicas-input"
                style={{ ...inputStyle, width: 100 }}
                type="number"
                min={0}
                max={50}
                placeholder={String(pool.replicas)}
                value={replicas}
                onChange={(e) => setReplicas(e.target.value)}
              />
              <button
                type="button"
                data-testid="pool-scale"
                style={{ ...primaryButton, opacity: replicas === '' || busy ? 0.5 : 1 }}
                disabled={replicas === '' || busy}
                onClick={() => void act(() => api.patchSandboxPool(pool.id, { replicas: Number(replicas) }))}
              >
                Scale
              </button>
              {pool.status !== 'retired' ? (
                <button
                  type="button"
                  data-testid="pool-retire"
                  style={secondaryButton}
                  disabled={busy}
                  onClick={() => void act(() => api.patchSandboxPool(pool.id, { status: 'retired' }))}
                >
                  Retire (drain)
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="pool-resume"
                  style={secondaryButton}
                  disabled={busy}
                  onClick={() => void act(() => api.patchSandboxPool(pool.id, { status: 'ready' }))}
                >
                  Un-retire
                </button>
              )}
              <button
                type="button"
                data-testid="pool-delete"
                style={secondaryButton}
                disabled={busy}
                onClick={() => {
                  const typed = window.prompt(
                    `Delete drains nothing by itself: ${pool.active} run(s) active. Type the pool name to delete:\n${pool.pool_name}`,
                  );
                  if (typed !== pool.pool_name) return;
                  void act(() => api.deleteSandboxPool(pool.id));
                }}
              >
                Delete
              </button>
            </div>
            {error && <div style={{ fontSize: 12, color: 'var(--danger, #c0392b)' }}>{error}</div>}
            <div style={{ fontSize: 12, color: 'var(--text-soft)', lineHeight: 1.5 }}>
              Retire drains active leases and stops new claims. Delete removes the pool and its
              template — conversations pinned to this bundle will fall back to the environment
              default (refused while runs are active unless forced via the API).
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
